import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MapPin, Heart, Calendar, Info, Bookmark, BookmarkX, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface SavedAdoptionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savedAdoptions: any[];
  onRefresh: () => void;
}

export default function SavedAdoptionsModal({ 
  open, 
  onOpenChange, 
  savedAdoptions,
  onRefresh 
}: SavedAdoptionsModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const getAgeText = (age?: number) => {
    if (!age) return '';
    return age === 1 ? '1 anno' : `${age} anni`;
  };

  const handleRemoveFromSaved = async (postId: string) => {
    if (!user) return;
    
    setRemovingId(postId);
    try {
      const { error } = await supabase
        .from('adoption_saves')
        .delete()
        .eq('adoption_post_id', postId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Rimosso dai salvati",
        description: "L'annuncio di adozione è stato rimosso dai tuoi salvati",
      });

      onRefresh();
    } catch (error) {
      console.error('Error removing from saved:', error);
      toast({
        title: "Errore",
        description: "Impossibile rimuovere l'annuncio",
        variant: "destructive",
      });
    } finally {
      setRemovingId(null);
    }
  };

  const handleViewInAdoptions = () => {
    onOpenChange(false);
    window.location.href = '/app/adoption';
  };

  const handleContactOwner = async (post: any) => {
    if (!user) {
      toast({
        title: "Accesso richiesto",
        description: "Devi effettuare l'accesso per contattare il proprietario",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create or find existing conversation
      const { data: existingConversations, error: fetchError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          conversations (
            id,
            type,
            conversation_participants!inner (
              user_id
            )
          )
        `)
        .eq('user_id', user.id);

      if (fetchError) throw fetchError;

      // Find a direct conversation with this user
      let conversationId = null;
      
      for (const participation of existingConversations || []) {
        const conversation = participation.conversations;
        if (conversation?.type === 'direct') {
          const participants = conversation.conversation_participants;
          if (participants.length === 2) {
            const otherParticipant = participants.find(p => p.user_id !== user.id);
            if (otherParticipant?.user_id === post.user_id) {
              conversationId = conversation.id;
              break;
            }
          }
        }
      }

      // If no conversation exists, create one
      if (!conversationId) {
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            type: 'direct',
            name: null,
            created_by: user.id
          })
          .select()
          .single();

        if (createError) throw createError;

        conversationId = newConversation.id;

        // Add participants
        const { error: participantsError } = await supabase
          .from('conversation_participants')
          .insert([
            { conversation_id: conversationId, user_id: user.id },
            { conversation_id: conversationId, user_id: post.user_id }
          ]);

        if (participantsError) throw participantsError;
      }

      // Navigate to the conversation
      window.location.href = `/app/chat/${conversationId}`;
    } catch (error) {
      console.error('Error creating/finding conversation:', error);
      toast({
        title: "Errore",
        description: "Errore nell'aprire la chat",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Bookmark className="w-5 h-5 text-primary" />
            <span>Adozioni Salvate ({savedAdoptions.length})</span>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          {savedAdoptions.length === 0 ? (
            <div className="text-center py-12">
              <Bookmark className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Nessuna adozione salvata</h3>
              <p className="text-muted-foreground mb-6">
                Salva gli annunci di adozione che ti interessano per rivederli più tardi
              </p>
              <Button onClick={handleViewInAdoptions}>
                <Heart className="w-4 h-4 mr-2" />
                Esplora Adozioni
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {savedAdoptions.map((post) => (
                <Card key={post.id} className="glass border-white/20 hover:border-white/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex space-x-4">
                      {/* Pet Image */}
                      <div className="flex-shrink-0">
                        <div className="w-24 h-24 rounded-lg overflow-hidden">
                          {post.image_url ? (
                            <img 
                              src={post.image_url} 
                              alt={post.pet_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-muted flex items-center justify-center">
                              <Heart className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Pet Info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-white text-lg">{post.title}</h3>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <span className="font-medium text-white">{post.pet_name}</span>
                              <span>•</span>
                              <span>{post.species}</span>
                              {post.breed && (
                                <>
                                  <span>•</span>
                                  <span>{post.breed}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <Badge className="bg-green-500/20 text-green-700 border-green-500/20">
                            Disponibile
                          </Badge>
                        </div>

                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          {post.age && <span>{getAgeText(post.age)}</span>}
                          {post.size && (
                            <>
                              <span>•</span>
                              <span>Taglia {post.size.toLowerCase()}</span>
                            </>
                          )}
                          {post.gender && (
                            <>
                              <span>•</span>
                              <span>{post.gender}</span>
                            </>
                          )}
                        </div>

                        <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          <span>{post.location}</span>
                        </div>

                        {post.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {post.description}
                          </p>
                        )}

                        {/* Owner info */}
                        {post.profiles && (
                          <div className="flex items-center space-x-2 pt-2">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={post.profiles.avatar_url} />
                              <AvatarFallback className="text-xs">
                                {post.profiles.display_name?.[0] || post.profiles.username?.[0] || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-xs text-muted-foreground">
                              {post.profiles.display_name || `@${post.profiles.username}`}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-3">
                          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            <span>Pubblicato {format(new Date(post.created_at), 'dd MMM', { locale: it })}</span>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleContactOwner(post)}
                              className="text-xs"
                            >
                              <MessageCircle className="w-3 h-3 mr-1" />
                              Contatta
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveFromSaved(post.id)}
                              disabled={removingId === post.id}
                              className="text-xs text-red-400 hover:text-red-300"
                            >
                              <BookmarkX className="w-3 h-3 mr-1" />
                              {removingId === post.id ? 'Rimuovo...' : 'Rimuovi'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>

        {savedAdoptions.length > 0 && (
          <div className="flex justify-between items-center pt-4 border-t border-white/10">
            <p className="text-sm text-muted-foreground">
              {savedAdoptions.length} {savedAdoptions.length === 1 ? 'adozione salvata' : 'adozioni salvate'}
            </p>
            <Button onClick={handleViewInAdoptions} variant="outline">
              <Heart className="w-4 h-4 mr-2" />
              Esplora Altre Adozioni
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}